import { OPTIONAL_CATEGORIES, type ConsentBasis, type ConsentCategory, type ConsentClientConfig, type OptionalCategory, type StoredConsent } from "../types";

/**
 * Browser-side consent state: the cms_consent cookie, who is granted what, and
 * a tiny event bus. Kept dependency-free so the public bundle stays small and
 * modules/themes can import it through @cms/module-api/client.
 */

export const CONSENT_COOKIE = "cms_consent";
export const CONSENT_EVENT = "cms:consent";
export const CONSENT_OPEN_EVENT = "cms:consent-open";
/** detail: { action: "accept_all" | "reject_all", source } — lets page content set a choice through the runtime. */
export const CONSENT_SET_EVENT = "cms:consent-set";

/** Open the cookie preferences dialog (footer links, policy page, module UI). */
export function openConsentPreferences(): void {
  window.dispatchEvent(new Event(CONSENT_OPEN_EVENT));
}

type ServiceInfo = { category: ConsentCategory; basis: ConsentBasis };

declare global {
  interface Window {
    __cmsConsentConfig?: ConsentClientConfig;
    __cmsConsentState?: StoredConsent | null;
    __cmsConsentGpc?: boolean;
    cmsConsent?: {
      get: () => StoredConsent | null;
      granted: (serviceId: string) => boolean;
      open: () => void;
      onChange: (callback: (state: StoredConsent | null) => void) => () => void;
    };
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const b64encode = (value: string) => btoa(unescape(encodeURIComponent(value))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
const b64decode = (value: string) => decodeURIComponent(escape(atob(value.replace(/-/g, "+").replace(/_/g, "/"))));

export function readStoredConsent(): StoredConsent | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${CONSENT_COOKIE}=([^;]*)`));
  if (!match) return null;
  try {
    const parsed = JSON.parse(b64decode(match[1])) as StoredConsent;
    return parsed && parsed.v === 1 && typeof parsed.id === "string" && typeof parsed.rev === "string" ? parsed : null;
  } catch {
    return null;
  }
}

/** A stored choice only counts for the current revision and within its lifetime. */
export function validConsent(stored: StoredConsent | null, config: Pick<ConsentClientConfig, "revision" | "expiryDays">): StoredConsent | null {
  if (!stored || stored.rev !== config.revision) return null;
  if (stored.ts + config.expiryDays * 86_400_000 < Date.now()) return null;
  return stored;
}

export function writeStoredConsent(state: StoredConsent, expiryDays: number): void {
  const secure = location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${CONSENT_COOKIE}=${b64encode(JSON.stringify(state))}; Max-Age=${Math.round(expiryDays * 86400)}; Path=/; SameSite=Lax${secure}`;
}

export function newConsentId(): string {
  const source = globalThis.crypto as Crypto;
  if (typeof source.randomUUID === "function") return source.randomUUID();
  const bytes = source.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function serviceIndex(config: ConsentClientConfig): Map<string, ServiceInfo> {
  const map = new Map<string, ServiceInfo>();
  for (const category of config.categories) for (const service of category.services) map.set(service.id, { category: category.id, basis: service.basis });
  return map;
}

/**
 * The single rule for "may this service run?":
 * necessary/exempt → always; a per-service choice wins; legitimate interest →
 * on unless the visitor objected; consent → only after an explicit yes.
 */
export function isGranted(serviceId: string, config: ConsentClientConfig, stored: StoredConsent | null): boolean {
  const info = serviceIndex(config).get(serviceId);
  if (!info) return false;
  if (info.category === "necessary" || info.basis === "exempt") return true;
  const valid = validConsent(stored, config);
  if (valid?.s && typeof valid.s[serviceId] === "boolean") return valid.s[serviceId];
  const category = info.category as OptionalCategory;
  if (info.basis === "legitimate_interest") return valid?.c[category] !== false;
  return valid?.c[category] === true;
}

export function categoriesFor(choice: "accept_all" | "reject_all", config: ConsentClientConfig): StoredConsent["c"] {
  const present = new Set(config.categories.map((category) => category.id));
  return Object.fromEntries(OPTIONAL_CATEGORIES.filter((category) => present.has(category)).map((category) => [category, choice === "accept_all"]));
}

export function emitConsentChange(state: StoredConsent | null): void {
  window.__cmsConsentState = state;
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: state }));
}

export function onConsentChange(callback: (state: StoredConsent | null) => void): () => void {
  const listener = (event: Event) => callback((event as CustomEvent<StoredConsent | null>).detail);
  window.addEventListener(CONSENT_EVENT, listener);
  return () => window.removeEventListener(CONSENT_EVENT, listener);
}

/** Granted check for code that only has the global config (embeds, module scripts). */
export function grantedNow(serviceId: string): boolean {
  const config = typeof window !== "undefined" ? window.__cmsConsentConfig : undefined;
  if (!config) return false;
  return isGranted(serviceId, config, window.__cmsConsentState ?? readStoredConsent());
}
